import { forwardRef } from 'react';

import { Icon } from '../../../shared/components/Icon';
import type { PublicSettings } from '../types';

export interface SidebarProps {
  readonly settings: PublicSettings;
  readonly sections: readonly { readonly id: string; readonly label: string }[];
  readonly activeSectionId: string;
}

/**
 * The left column.
 *
 * Above the split breakpoint this is fixed to the viewport and does not scroll — it is
 * a flex child of a `100dvh` row with `overflow: hidden`, so it is physically incapable
 * of moving. That avoids `position: sticky` entirely, which depends on the document
 * scrolling and does nothing when the host sizes the frame to content.
 *
 * Below the breakpoint it becomes an ordinary page header and the document scrolls as
 * one.
 */
export const Sidebar = forwardRef<HTMLElement, SidebarProps>(function Sidebar(
  { settings, sections, activeSectionId },
  ref,
) {
  return (
    <header
      ref={ref}
      className="relative flex w-full flex-col justify-between px-0 pt-20 pb-10 split:h-full split:w-[48%] split:flex-none split:overflow-y-auto split:pt-[100px] split:pb-[88px] split:[overscroll-behavior:contain] split:[scrollbar-width:none]"
    >
      <div>
        <h1 className="m-0 text-[clamp(42px,6.2vw,58px)] font-bold leading-[1.05] tracking-name text-text">
          {settings.displayName}
        </h1>

        <p className="mt-3 mb-0 text-[clamp(17px,2.2vw,20px)] font-medium tracking-[-0.01em] text-text">
          {settings.roleLine}
        </p>

        <p className="mt-4 mb-0 max-w-[32ch] text-body font-light">{settings.pitch}</p>

        {settings.isAvailable && (
          <p className="mt-[22px] mb-0 inline-flex items-center gap-[9px] text-[11px] font-medium uppercase tracking-[0.14em] text-live">
            <span
              aria-hidden="true"
              className="relative size-[7px] flex-none rounded-full bg-live motion-safe:after:absolute motion-safe:after:inset-0 motion-safe:after:animate-ping-slow motion-safe:after:rounded-full motion-safe:after:bg-current"
            />
            {settings.availabilityText}
          </p>
        )}

        {/* Hidden below the breakpoint: each section grows its own sticky heading there. */}
        <nav className="mt-[52px] hidden split:block" aria-label="Sections">
          <ul className="m-0 list-none p-0">
            {sections.map((section) => {
              const isActive = section.id === activeSectionId;

              return (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    aria-current={isActive ? 'true' : undefined}
                    className={`group flex items-center gap-4 py-3 text-[11.5px] font-semibold uppercase tracking-label transition-colors duration-250 ${
                      isActive ? 'text-text' : 'text-faint hover:text-text'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`h-px transition-[width,background-color] duration-300 ease-reveal ${
                        isActive
                          ? 'w-16 bg-accent'
                          : 'w-8 bg-line-strong group-hover:w-16 group-hover:bg-accent'
                      }`}
                    />
                    {section.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      <div className="mt-11 flex items-center gap-5">
        <a
          href={settings.gitHubUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="GitHub"
          className="text-muted transition-[color,transform] duration-250 hover:-translate-y-[3px] hover:text-accent"
        >
          <Icon name="github" size={21} />
        </a>
        <a
          href={settings.linkedInUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="LinkedIn"
          className="text-muted transition-[color,transform] duration-250 hover:-translate-y-[3px] hover:text-accent"
        >
          <Icon name="linkedin" size={21} />
        </a>
        <a
          href={`mailto:${settings.email}`}
          aria-label={`Email ${settings.displayName}`}
          className="text-muted transition-[color,transform] duration-250 hover:-translate-y-[3px] hover:text-accent"
        >
          <Icon name="mail" size={21} />
        </a>
      </div>
    </header>
  );
});
